import React, { useState, useEffect, useCallback } from 'react';
import { Card, InputNumber, Row, Col, Typography, Statistic, Slider, Select, Divider, Alert } from 'antd';
import { DollarOutlined, TrophyOutlined, CalendarOutlined, CalculatorOutlined } from '@ant-design/icons';
import { useLocation } from '../contexts/LocationContext';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

interface ROIResult {
  monthlySavings: number;
  annualSavings: number;
  paybackMonths: number;
  roi: number;
  threeYearValue: number;
}

export const ROICalculator: React.FC = () => {
  const { city, state } = useLocation();
  const [monthlyUsage, setMonthlyUsage] = useState<number>(1000); // kWh
  const [currentBill, setCurrentBill] = useState<number>(150); // dollars
  const [optimizationLevel, setOptimizationLevel] = useState<number>(15); // percentage
  const [subscriptionTier, setSubscriptionTier] = useState<'basic' | 'premium'>('premium');
  const [result, setResult] = useState<ROIResult | null>(null);
  const [loading, setLoading] = useState(false);

  const calculateROI = useCallback(async () => {
    setLoading(true);
    try {
      // Calculate savings based on optimization level
      const savingsPercentage = optimizationLevel / 100;
      const monthlySavings = currentBill * savingsPercentage;
      const annualSavings = monthlySavings * 12;

      // Subscription costs
      const subscriptionCost = subscriptionTier === 'basic' ? 29 : 79;
      const annualSubscriptionCost = subscriptionCost * 12;

      // Net savings
      const netAnnualSavings = annualSavings - annualSubscriptionCost;

      // Payback period (assuming initial setup cost = subscription cost)
      const paybackMonths = netAnnualSavings > 0 
        ? (subscriptionCost / monthlySavings) 
        : 999;

      // ROI (annual return on investment)
      const roi = netAnnualSavings > 0 
        ? (netAnnualSavings / annualSubscriptionCost) * 100 
        : 0;

      // Three-year value
      const threeYearValue = (netAnnualSavings * 3);

      setResult({
        monthlySavings,
        annualSavings,
        paybackMonths: Math.min(paybackMonths, 120), // Cap at 10 years
        roi: Math.min(roi, 1000), // Cap at 1000%
        threeYearValue,
      });
    } catch (error) {
      console.error('ROI calculation error:', error);
    } finally {
      setLoading(false);
    }
  }, [monthlyUsage, currentBill, optimizationLevel, subscriptionTier]);

  useEffect(() => {
    calculateROI();
  }, [calculateROI]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  const getOptimizationDescription = (level: number) => {
    if (level < 10) return 'Conservative (time-of-use optimization)';
    if (level < 20) return 'Moderate (load shifting + efficiency)';
    if (level < 30) return 'Aggressive (full optimization + renewables)';
    return 'Maximum (complete energy transformation)';
  };

  return (
    <Card>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <CalculatorOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
        <Title level={3}>ROI Calculator</Title>
        <Paragraph type="secondary">
          Calculate your potential savings and return on investment
          {city && state && (
            <span> for {city}, {state}</span>
          )}
        </Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title="Your Current Usage" size="small">
            <div style={{ marginBottom: '24px' }}>
              <Text strong>Monthly Energy Usage (kWh)</Text>
              <InputNumber
                style={{ width: '100%', marginTop: '8px' }}
                value={monthlyUsage}
                onChange={(value) => setMonthlyUsage(value || 1000)}
                min={100}
                max={100000}
                step={100}
                formatter={(value) => `${value} kWh`}
                parser={(value) => {
                  const num = value?.replace(' kWh', '') || '1000';
                  return parseFloat(num) || 1000;
                }}
              />
              <Text type="secondary" style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}>
                Average US household: 900-1,000 kWh/month
              </Text>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <Text strong>Current Monthly Bill ($)</Text>
              <InputNumber
                style={{ width: '100%', marginTop: '8px' }}
                value={currentBill}
                onChange={(value) => setCurrentBill(value || 150)}
                min={50}
                max={10000}
                step={10}
                formatter={(value) => `$${value}`}
                parser={(value) => {
                  const num = value?.replace('$', '') || '150';
                  return parseFloat(num) || 150;
                }}
              />
              <Text type="secondary" style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}>
                Your actual monthly utility bill
              </Text>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <Text strong>Optimization Level: {optimizationLevel}%</Text>
              <Slider
                value={optimizationLevel}
                onChange={(value) => setOptimizationLevel(value)}
                min={5}
                max={35}
                step={5}
                marks={{
                  5: '5%',
                  15: '15%',
                  25: '25%',
                  35: '35%',
                }}
                style={{ marginTop: '8px' }}
              />
              <Text type="secondary" style={{ display: 'block', marginTop: '8px', fontSize: '12px' }}>
                {getOptimizationDescription(optimizationLevel)}
              </Text>
            </div>

            <div>
              <Text strong>Subscription Tier</Text>
              <Select
                style={{ width: '100%', marginTop: '8px' }}
                value={subscriptionTier}
                onChange={(value) => setSubscriptionTier(value)}
              >
                <Option value="basic">
                  Starter - $29/month ({formatCurrency(29 * 12)}/year)
                </Option>
                <Option value="premium">
                  Professional - $79/month ({formatCurrency(79 * 12)}/year)
                </Option>
              </Select>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Your ROI Results" size="small" loading={loading}>
            {result && (
              <div>
                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                  <Col span={12}>
                    <Statistic
                      title="Monthly Savings"
                      value={result.monthlySavings}
                      prefix={<DollarOutlined />}
                      valueStyle={{ color: '#3f8600', fontSize: '20px' }}
                      formatter={(value) => formatCurrency(Number(value))}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Annual Savings"
                      value={result.annualSavings}
                      prefix={<DollarOutlined />}
                      valueStyle={{ color: '#3f8600', fontSize: '20px' }}
                      formatter={(value) => formatCurrency(Number(value))}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Payback Period"
                      value={result.paybackMonths}
                      suffix="months"
                      prefix={<CalendarOutlined />}
                      valueStyle={{ 
                        color: result.paybackMonths < 12 ? '#3f8600' : '#1890ff',
                        fontSize: '20px'
                      }}
                      precision={1}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Annual ROI"
                      value={result.roi}
                      suffix="%"
                      prefix={<TrophyOutlined />}
                      valueStyle={{ 
                        color: result.roi > 100 ? '#3f8600' : '#1890ff',
                        fontSize: '20px'
                      }}
                      precision={1}
                    />
                  </Col>
                </Row>

                <Divider />

                <Alert
                  message="3-Year Value"
                  description={
                    <div>
                      <Text strong style={{ fontSize: '24px', color: '#3f8600' }}>
                        {formatCurrency(result.threeYearValue)}
                      </Text>
                      <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
                        Net savings over 3 years (after subscription costs)
                      </Text>
                    </div>
                  }
                  type="success"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />

                {result.paybackMonths < 12 && (
                  <Alert
                    message="Excellent ROI"
                    description={`Your investment pays for itself in ${result.paybackMonths.toFixed(1)} months, then you're saving ${formatCurrency(result.monthlySavings)}/month!`}
                    type="success"
                    showIcon
                  />
                )}

                {result.paybackMonths >= 12 && result.paybackMonths < 24 && (
                  <Alert
                    message="Good ROI"
                    description={`Payback in ${result.paybackMonths.toFixed(1)} months. After that, you'll save ${formatCurrency(result.monthlySavings)}/month.`}
                    type="info"
                    showIcon
                  />
                )}

                {result.paybackMonths >= 24 && (
                  <Alert
                    message="Consider Higher Optimization"
                    description="Try increasing your optimization level to see better returns, or contact us for a custom analysis."
                    type="warning"
                    showIcon
                  />
                )}

                <Alert
                  message="Important: Projections Based on Estimates"
                  description="These calculations are projections based on the inputs you provided. Your actual savings may vary based on your specific energy usage patterns, equipment efficiency, utility rates, and how effectively you implement our recommendations. We use conservative estimates to ensure realistic expectations."
                  type="info"
                  showIcon
                  style={{ marginTop: '16px' }}
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Divider />

      <div style={{ textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          * Calculations are estimates based on average optimization results. Actual savings may vary based on your specific usage patterns, utility rates, and implementation.
        </Text>
      </div>
    </Card>
  );
};

export default ROICalculator;

